import React, { useEffect, useMemo } from 'react';
import { Button } from '../atomic/buttons';
import {
    SelectField,
    SelectFieldOption,
    SelectFieldOptionGroup,
} from '../atomic/forms';
import { Icons } from '../atomic/icons';
import { useGet } from '../../hooks/useApi';
import { formatName } from '../../models/FileModel';
import { getFolderLink, useNavigateToItem } from '../atomic/links';
import { FilesAPI } from '../../APIs/filesApi';
import {
    DeleteFilesFileInfo,
    useFileDeleteDialog,
} from '../folderview/DeleteFilesDialog';
import { useNavigate } from 'react-router';
import { FilterModel } from '../report/Report';

const limit = 10;

export interface ReelFileMenuProps {
    projectId: string;
    fileId: string;
    folderId: string;
    rows?: Array<string>;
    filter: FilterModel[];
}

export const ReelFileMenu = ({
    projectId,
    fileId,
    folderId,
    rows,
    filter,
}: ReelFileMenuProps) => {
    const files = useGet(FilesAPI.nonFrameIds({ folderId }));
    const fileIds = useMemo<Array<string>>(() => {
        if (!files.data?.data) {
            return [];
        }
        if (rows?.length === 0) {
            return files.data.data;
        }
        return files.data.data.filter((file) => {
            return rows?.find((row) => row === file);
        });
    }, [files]);

    const item = useGet(FilesAPI.item(fileId));
    const [index, page, offset] = useMemo(() => {
        if (!fileIds) return [-2, null, undefined];
        const index = fileIds.indexOf(fileId);
        if (index === -1) return [-1, null, undefined];
        const page = (index / limit) | 0;
        return [index, page, page * limit];
    }, [fileIds, fileId]);
    const pageIds = fileIds.slice(offset ?? 0, (offset ?? 0) + limit) ?? [];
    const pageFiles = useGet(
        offset !== null
            ? rows?.length === 0
                ? FilesAPI.pagedNonFrames({
                      parentFolderId: folderId,
                      limit,
                      offset,
                  })
                : FilesAPI.fileIds({ ids: pageIds })
            : null,
        { keepPreviousData: false }
    );

    const pageIdsStr = pageIds.join(',');
    const navigateToItem = useNavigateToItem();
    const navigate = useNavigate();
    const [next, prev, nextPage, prevPage] = useMemo(() => {
        if (index < 0) return [];
        if (!fileIds) return [];
        const ids = fileIds;
        const nextFile = ids[index + 1];
        const prevFile = ids[index - 1];
        const next = nextFile
            ? () => navigateToItem({ fileId: nextFile })
            : undefined;
        const prev = prevFile
            ? () => navigateToItem({ fileId: prevFile })
            : undefined;
        const prevPage = offset !== undefined ? ids[offset - 1] : null;
        const nextPage = offset !== undefined ? ids[offset + limit] : null;
        return [next, prev, nextPage, prevPage];
    }, [files, index, offset]);
    const selectOptions = useMemo(() => {
        const files = pageFiles.data?.data;
        const result: Array<SelectFieldOptionGroup | SelectFieldOption> = [];
        const pages: SelectFieldOption[] = pageIds.map((fileId) => {
            const file = files?.find((file) => file.fileId === fileId);
            return {
                value: fileId,
                text: file ? formatName(file) : ' …',
            };
        });
        result.push({
            label: `ページ ${(page ?? 0) + 1}`,
            options: pages,
        });
        result.push({
            text: '前のページ',
            value: prevPage ?? '',
            disabled: !prevPage,
        });
        result.push({
            text: '次のページ',
            value: nextPage ?? '',
            disabled: !nextPage,
        });
        return result;
    }, [pageIdsStr, pageFiles.data?.data, page, prevPage, nextPage]);
    const deleteDialog = useFileDeleteDialog(
        {
            onPreDelete() {
                if (next) next();
                else if (prev) prev();
                else navigate(getFolderLink(folderId));
            },
        },
        [next, prev, folderId]
    );

    const navigateReport = () => {
        navigate(`/app/report/${projectId}`, { state: { filter } });
    };

    const fileInfo: DeleteFilesFileInfo = {
        id: fileId,
        isFile: true,
        name: `${item.data?.data?.name ?? '...'}`,
    };
    return (
        <div className="reel--navigation">
            <Button icon={Icons.Left} disabled={!prev} onClick={prev} />
            {selectOptions ? (
                <SelectField
                    options={selectOptions}
                    value={fileId}
                    _onChange={(fileId: string) => navigateToItem({ fileId })}
                />
            ) : (
                <div className="reel--filelist--loading">...</div>
            )}
            <Button
                icon={Icons.Delete}
                onClick={() =>
                    deleteDialog.show({ parentId: folderId, files: [fileInfo] })
                }
            />
            <Button icon={Icons.Right} disabled={!next} onClick={next} />
            <Button icon={Icons.Report} onClick={() => navigateReport()} />
            {deleteDialog.dialog}
        </div>
    );
};
